import React, {useState, useRef} from 'react';
import {createViewConfig} from '@sail/engine';
import type {IconProps} from '../Icon';
import {Icon} from '../Icon';
import type {IconSet, DynamicIconAsset} from './types';

export type DynamicIconProps = Omit<IconProps, 'icon'> & {
  icon: DynamicIconAsset;
};

export const DynamicIconConfig = createViewConfig({
  // The configured view is the internal icon so users have direct access to the
  // icon asset as they would expect. In this case, the `icon` prop is always
  // the asset so we need to tweak the props
  props: {} as DynamicIconProps,
  name: 'DynamicIcon',
  flattens: true,
});

const iconSetCache = new Map<string, IconSet>();

/**
 * Similar to `Icon` but support lazily loading an icon from an
 * icon set based off a loader
 */
export const DynamicIcon = DynamicIconConfig.createView(({icon, ...props}) => {
  const cachedIcons = iconSetCache.get(icon.loader.name);
  const iconsLoaded = useRef(!!cachedIcons);
  const [icons, setIcons] = useState<IconSet | undefined>(cachedIcons);

  if (!iconsLoaded.current) {
    iconsLoaded.current = true;

    icon.loader.load().then((icons: IconSet) => {
      setIcons(icons);
      iconSetCache.set(icon.loader.name, icons);
    });
  }

  const iconAsset = icons?.[icon.name];

  return (
    <Icon
      {...props}
      icon={iconAsset?.svg ? iconAsset : icon.loader.defaultIcon}
    />
  );
});
